import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { injectIntl } from 'react-intl'
import Spinner from '../../../util/spinner-shim'
import { useStateWithCallback } from '../../../util/CustomHooks'

import AppButton from '../../partials/Navigation/AppButton'

import Footer from '../../partials/Footer'
import { isNumber } from '../../partials/Inputs/Inputs'

import NavigationBar from '../../partials/NavigationBar'

import styles from '../../../../css/home.module.css'
import styles2 from '../../../../css/devicePage.module.css'

import AppStore from '../../../stores/AppStore'
import AppDispatcher from '../../../dispatchers/AppDispatcher'
import { CapitalButton } from '../../partials/Navigation/Buttons'
import { useNavigate, useParams } from 'react-router-dom'
import withAppStore from '../../../util/withAppStore'
import PropTypes from 'prop-types'

const uniqueString = (group) => `${group.name}-${group.icon}-${group.deviceGroup}`

function CustomerReportSettingsGroupsPage (props) {
  const navigate = useNavigate()
  const { customerID, ruleID, deviceType } = useParams()

  const rulE = AppStore.getNewRule()
  const { transport: { query: { deviceGroup: ruleGroupIDs = [] } = {} } = {} } =
    rulE
  const initialState = {
    groups: [],
    groupsReady: false,
    deviceGroup: ruleGroupIDs,
    rule: rulE,
    saved: false,
    changed: false,
    statusMessageIsVisible: false,
    selectAllState: false,
  }
  const [groupState, setGroupState] =
    useStateWithCallback(initialState)
  const { groups, groupsReady, selectAllState } = groupState

  const handleChange = (field, value, callback) => {
    const newState = {
      ...groupState,
      [field]: isNumber(value) ? Number(value) : value,
      statusMessageIsVisible: false,
    }
    setGroupState(newState, () => {
      if (callback) callback()
    })
  }

  const allChecked = (theGroups) => {
    const checkArray = theGroups.map((g) => {
      if (g.checked === undefined) {
        return false
      }
      return g.checked
    })

    if (checkArray.includes(false)) {
      return false
    }
    return true
  }

  const getGroups = () => {
    /**
     * sets up groups to be displayed on the page
     */
    if (!groups || !groups.length) {
      const setGroups = (fromFetch = false) => {
        const userGroups = AppStore.getGroups()
        const groupsArray = Array.isArray(userGroups) ? userGroups : []
        if (groupsArray.length) {
          groupsArray.forEach((group) => {
            const tempGroup = group
            if (ruleGroupIDs.includes(group.id)) {
              tempGroup.checked = true
            } else {
              delete tempGroup.checked
            }
          })

          const sortedGroups = groupsArray.sort((a, b) => uniqueString(a).localeCompare(uniqueString(b)))
          const selectedCheck = allChecked(sortedGroups)

          setGroupState({
            ...groupState,
            groups: sortedGroups,
            groupsReady: true,
            selectAllState: selectedCheck,
          })
        } else if (fromFetch) {
          // fetch resolved but no groups returned — mark ready so we
          // render an empty state instead of an infinite spinner.
          setGroupState({
            ...groupState,
            groups: [],
            groupsReady: true,
          })
        }
        // else: synchronous probe before the dispatch — store was just
        // cleared by resetGroups(), so do nothing and wait for the
        // fetch callback to settle the ready state.
      }

      setGroups()

      const { id: userID } = AppStore.getUser()
      const { applications } = AppStore.getCustomer()

      if (applications) {
        const allUserGroups = applications
          .map((app) => app.groupID)
          .toString()

        // get the groups this user has permissions for that contain the deviceType
        AppDispatcher.dispatch({
          action: 'get-groups',
          params: {
            all: true,
            customerID,
            deviceType,
            groupID: allUserGroups,
            userID,
          },
          callback: () => setGroups(true),
        })
      }
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    AppStore.resetGroups()
    AppStore.addChangeListener(handleChange)
    getGroups()
    return () => {
      AppStore.removeChangeListener(handleChange)
    }
  }, [])

  const goBack = () => {
    navigate(`/${customerID}/settings/reports/${!ruleID || ruleID === 'new' ? 'add' : ruleID}`)
  }

  const saveGroups = () => {
    const { groups: groupSave, rule: saveRule } = groupState
    const { transport = {} } = saveRule

    if (!transport.query) {
      transport.query = {}
    }

    const deviceGroup =
      groupSave
        .filter((group) => group.checked)
        .map((group) => group.id) || []

    // if all groups were checked clear deviceGroup
    transport.query.deviceGroup = deviceGroup

    // temporary flag to indicate change
    transport.query.deviceGroupChanged = true
    AppDispatcher.dispatch({
      action: 'store-new-rule',
      noEvent: true,
      rule: saveRule,
    })
  }

  const toggleChecked = (group) => {
    const { groups: theGroups } = groupState
    const toggleGroup = group
    toggleGroup.checked = !toggleGroup.checked
    const isAllChecked = allChecked(theGroups)
    setGroupState(
      {
        ...groupState,
        groups: theGroups,
        changed: true,
        selectAllState: isAllChecked,
      },
      saveGroups
    )
  }

  const selectAll = () => {
    const { groups: theGroups } = groupState
    const selectGroups = theGroups
    const isAllChecked = allChecked(theGroups)
    if (isAllChecked) {
      selectGroups.map((g) => (g.checked = false))
    } else {
      selectGroups.map((g) => (g.checked = true))
    }

    setGroupState(
      {
        ...groupState,
        groups: theGroups,
        changed: true,
        selectAllState: !isAllChecked,
      },
      saveGroups
    )
  }

  const {
    auth: { logout } = {},
    intl: { formatMessage },
  } = props

  let groupsButtons

  if (!groupsReady) {
    groupsButtons = [
      <Spinner
        key='spinner'
        name='double-bounce'
        style={{ width: '30px', marginLeft: 'auto', marginRight: 'auto' }}
      />,
    ]
  } else if (groups && groups.length) {
    groupsButtons = groups
      .sort((a, b) => uniqueString(a).localeCompare(uniqueString(b)))
      .map((group) => (
        <AppButton
          checked={group.checked}
          icon={group.icon}
          label={group.name || group.deviceGroup}
          key={uniqueString(group)}
          onClick={() => toggleChecked(group)}
        />
      ))
  } else {
    groupsButtons = []
  }
  let message
  let id
  let buttonStyles = styles2.selectAllButton

  if (selectAllState) {
    message = 'Deselect All'
    id = 'setting.select.none'
    buttonStyles = styles2.deselectAllButton
  } else {
    id = 'setting.select.all'
    message = 'Select All'
    buttonStyles = styles2.selectAllButton
  }
  const selectAllButton = (
    <CapitalButton
      text={formatMessage({
        id,
        defaultMessage: message,
        description: '',
      }).toUpperCase()}
      disabled={!groups || !groups.length}
      buttonFunction={selectAll}
      hSize='h5'
      style={`${buttonStyles} ${styles2.selectAllButtonStyles}`}
      buttonStyles={{}}
    />
  )

  const translate = (translateId, defaultMessage) => formatMessage({ id: translateId, defaultMessage })
  const subNavTitle = translate('reports.groups.button', 'Groups In Report')

  const {
    pages: { CustomerReportSettingsGroupsPage: title = subNavTitle } = {},
  } = props
  const saveButtonStyle = `${styles.saveButton} saveButton`
  let saveButton
  if (groups && groups.length) {
    saveButton = (
      <CapitalButton
        buttonFunction={goBack}
        hSize='h2'
        style={saveButtonStyle}
        text={translate('setting.save', 'SAVE')}
      />
    )
  }

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        logOut={() => logout(navigate)}
        backFunc={goBack}
        name={subNavTitle}
      />
      <div className={styles.pageWidth}>
        <div className={styles.pageContainer}>
          {selectAllButton}
          <div className={styles.list}>{groupsButtons}</div>
        </div>
        <div>
          <div>
            <div className={styles.pageContainer}>{saveButton}</div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

CustomerReportSettingsGroupsPage.propTypes = {
  pages: PropTypes.object,
  data: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
  route: PropTypes.object,
}

export default withAppStore(injectIntl(CustomerReportSettingsGroupsPage))
